import {
    Component,
    MeshComponent,
    Object3D,
    PhysXComponent,
    TextComponent,
} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {Inventory} from '../components/inventory.js';
import {TextBubbles} from '../components/text-bubbles.js';
import {InspectNote} from '../components/inspect-note.js';
import {TextFade} from '../components/text-fade.js';
import {InspectWorldObject} from '../components/inspect-world-object.js';
import {InvestigationBoard} from '../components/investigation-board.js';
import {FlatMaterial} from '../types/materials.js';
import {GameManagerInstance, SceneNames} from '../managers/gamemanager.js';
import {GA} from '../utils/ga-utils.js';
import {setComponentsActive} from '../utils/activate-children.js';

enum Level2State {
    start,
    pickedUpPhoto,
    gatheringFiles,
    gatheredFiles,
}

/**
 * level-manager-scene2
 *
 * Flow of the level and level states:
 * - There's knocking on the door.
 * - A photo is slipped under the door.
 * - The player picks up the photo
 * - Entry added to the todo list
 * - Task 1) The player needs to put the photo on the investigation board.
 * - Interacts with the investigation board.
 * - Text Shown: I think the Laughing man...
 * - Entry added to the todo list
 * - Task 2) Gather the files
 * - Photo is placed on the board
 * - Text Shown: This photo has an address...
 * - Task 1 crossed out from the todo list
 * - The player needs to search for the files
 * (Refine below)
 * - When the files are found they need to be put on the investigation board.
 * - Photos of murders need to be connected
 * - The player needs to go to bed.
 *
 * Optional does not realy change state:
 * - Grabbing the door handle shows message
 * - Picking up books in bookshelf shows message
 * - Picking up the phone shows message
 * - Picking up the laundry shows message
 */
export class LevelManagerScene2 extends Component {
    static TypeName = 'level-manager-scene2';
    /* Properties that are configurable in the editor */

    /* Sequence 1 - Door Photo */
    @property.object()
    player?: Object3D;
    @property.object()
    doorCollision?: Object3D;
    @property.object()
    doorPhoto?: Object3D;
    @property.object()
    seq01_01_text?: Object3D; // "This photo goes up on the board."
    @property.object()
    seq01_killerPhoto?: Object3D;
    @property.object()
    aptPhoto?: Object3D;
    @property.object()
    string_AptPhoto?: Object3D;
    @property.object()
    seq01_03_notepad?: Object3D;
    @property.object()
    seq01_02_text?: Object3D; // This photo has an address and 'Seek the Laughing Man' ...
    @property.object()
    gatheringFiles_Text?: Object3D; // I need to gather the rest of the files...
    @property.object()
    seq01_04_notepad?: Object3D;
    /* Crime Docs */
    @property.object()
    notepad_crimeDocFinal?: Object3D;
    @property.object()
    investigationBoard?: Object3D;
    @property.object()
    docsFinal_boardText?: Object3D; // Now to get all my evidence up on the board.
    /* Murder Images - Invistagation Board */
    @property.object()
    murderImages?: Object3D;
    @property.object()
    crimeCircle?: Object3D;
    @property.object()
    notepad5_InvestigationWall?: Object3D;
    @property.object()
    Strings_TextBubble01?: Object3D; // In each of these cases the victim had their mouth ...
    @property.object()
    Strings_TextBubble02?: Object3D; // The apartment in the photo is smack dab in the ...
    @property.object()
    Strings_TextBubble03?: Object3D; // For now, I better get to bed before the moon comes out.
    @property.object()
    bed?: Object3D;
    @property.object({required: true})
    crimeDocProgress!: Object3D;

    private stringsTextBubbleNum = 1;
    private seq01_killerPhotoPhysxComponent?: PhysXComponent;
    private seq01_killerPhotoTextBubbles?: TextBubbles;
    private seq01_01_TextComponent?: TextComponent;
    private seq01_01_TextFade?: TextFade;

    private docsFinalTextComponent?: TextComponent;
    private investigationBoardPhysx?: PhysXComponent;
    private crimeCircleMesh?: MeshComponent;
    private bedTextBubble?: TextBubbles;
    private doorCollisionPhysxComponent?: PhysXComponent;
    private playerInventory?: Inventory;
    private seq01_02_TextBubbles?: TextBubbles;
    private seq01_04_InspectNote?: InspectNote;
    private level2State = Level2State.start;
    private gatheringFilesBubbles?: TextBubbles;

    start() {
        /** Sequence 1 **/
        /* Deactive Texts & Collisions */

        if (!this.seq01_01_text) {
            throw new Error(
                `level-manager-scene2(${this.object.name}): seq01_01_text is not defined`
            );
        }
        if (!this.seq01_02_text) {
            throw new Error(
                `level-manager-scene2(${this.object.name}): seq01_02_text is not defined`
            );
        }
        if (!this.gatheringFiles_Text) {
            throw new Error(
                `level-manager-scene2(${this.object.name}): seq01_03_text is not defined`
            );
        }
        const tempTextBubbles = this.gatheringFiles_Text.getComponent(TextBubbles);
        if (!tempTextBubbles) {
            throw new Error(
                `level-manager-scene2(${this.object.name}):.gatheringFiles_Text does not have a TextBubbles`
            );
        }
        this.gatheringFilesBubbles = tempTextBubbles;

        if (!this.seq01_03_notepad) {
            throw new Error(
                `level-manager-scene2(${this.object.name}): seq01_03_notepad is not defined`
            );
        }
        if (!this.seq01_04_notepad) {
            throw new Error(
                `level-manager-scene2(${this.object.name}): seq01_04_notepad is not defined`
            );
        }
        if (!this.notepad_crimeDocFinal) {
            throw new Error(
                `level-manager-scene2(${this.object.name}): notepad_crimeDocFinal is not defined`
            );
        }
        if (!this.investigationBoard) {
            throw new Error(
                `level-manager-scene2(${this.object.name}): investigationBoard is not defined`
            );
        }
        const tempInvestigationBoardPhysx =
            this.investigationBoard.getComponent(PhysXComponent);
        if (!tempInvestigationBoardPhysx) {
            throw new Error(
                `level-manager-scene2(${this.object.name}): PhysXComponent is missing on investigationBoard`
            );
        }
        this.investigationBoardPhysx = tempInvestigationBoardPhysx;

        if (!this.docsFinal_boardText) {
            throw new Error(
                `level-manager-scene2(${this.object.name}): docsFinal_boardText is not defined`
            );
        }
        const tempDocsFinalTextComponent =
            this.docsFinal_boardText.getComponent(TextComponent);
        if (!tempDocsFinalTextComponent) {
            throw new Error(
                `level-manager-scene2(${this.object.name}): TextComponent is required on docsFinal_boardText`
            );
        }
        this.docsFinalTextComponent = tempDocsFinalTextComponent;
        this.docsFinalTextComponent.active = false;

        if (!this.crimeCircle) {
            throw new Error(
                `level-manager-scene2(${this.object.name}): crimeCircle is not defined`
            );
        }
        const tempCrimeCircleMesh = this.crimeCircle.getComponent(MeshComponent);
        if (!tempCrimeCircleMesh) {
            throw new Error(
                `level-manager-scene2(${this.object.name}): MeshComponent is required on crimeCircle`
            );
        }
        this.crimeCircleMesh = tempCrimeCircleMesh;
        this.crimeCircleMesh.active = false;

        if (!this.notepad5_InvestigationWall) {
            throw new Error(
                `level-manager-scene2(${this.object.name}): notepad5_InvestigationWall is not defined`
            );
        }
        if (!this.Strings_TextBubble01) {
            throw new Error(
                `level-manager-scene2(${this.object.name}): Strings_TextBubble01 is not defined`
            );
        }
        if (!this.Strings_TextBubble02) {
            throw new Error(
                `level-manager-scene2(${this.object.name}): Strings_TextBubble02 is not defined`
            );
        }
        if (!this.Strings_TextBubble03) {
            throw new Error(
                `level-manager-scene2(${this.object.name}): Strings_TextBubble03 is not defined`
            );
        }
        if (!this.bed) {
            throw new Error(
                `level-manager-scene2(${this.object.name}): bed is not defined`
            );
        }
        const tempBedTextBubble = this.bed.getComponent(TextBubbles);
        if (!tempBedTextBubble) {
            throw new Error(
                `level-manager-scene2(${this.object.name}): TextBubbles is missing on bed`
            );
        }
        this.bedTextBubble = tempBedTextBubble;

        const tempSeq01_01_TextComponent = this.seq01_01_text.getComponent(TextComponent);
        if (!tempSeq01_01_TextComponent) {
            throw new Error(
                `level-manager-scene2(${this.object.name}): TextComponent is required on seq01_01_text`
            );
        }
        this.seq01_01_TextComponent = tempSeq01_01_TextComponent;
        this.seq01_01_TextComponent.active = false;

        const tempSeq01_01_TextFade = this.seq01_01_text.getComponent(TextFade);
        if (!tempSeq01_01_TextFade) {
            throw new Error(
                `level-manager-scene2(${this.object.name}): TextFade is required on seq01_01_text`
            );
        }
        this.seq01_01_TextFade = tempSeq01_01_TextFade;

        if (!this.seq01_killerPhoto) {
            throw new Error(
                `level-manager-scene2(${this.object.name}): seq01_killerPhoto is not defined`
            );
        }
        const tempSeq01_killerPhotoPhysxComponent =
            this.seq01_killerPhoto.getComponent(PhysXComponent);
        if (!tempSeq01_killerPhotoPhysxComponent) {
            throw new Error(
                `level-manager-scene2(${this.object.name}): PhysXComponent is required on seq01_killerPhoto`
            );
        }
        this.seq01_killerPhotoPhysxComponent = tempSeq01_killerPhotoPhysxComponent;
        this.seq01_killerPhotoPhysxComponent.active = false;
        const tempSeq01_killerPhotoTextBubbles =
            this.seq01_killerPhoto.getComponent(TextBubbles);
        if (!tempSeq01_killerPhotoTextBubbles) {
            throw new Error(
                `level-manager-scene2(${this.object.name}): TextBubbles is required on seq01_killerPhoto`
            );
        }
        this.seq01_killerPhotoTextBubbles = tempSeq01_killerPhotoTextBubbles;

        /* Deactive ApartmentPhoto of InvestigationWall */
        if (!this.aptPhoto) {
            throw new Error(
                `level-manager-scene2(${this.object.name}): aptPhoto is not defined`
            );
        }
        for (const child of this.aptPhoto.children) {
            const aptPhotoChildMesh = child.getComponent(MeshComponent);
            if (!aptPhotoChildMesh) {
                throw new Error(
                    `level-manager-scene2(${this.object.name}): aptPhotoChildMesh is required on children of aptPhoto`
                );
            }
            aptPhotoChildMesh.active = false;
        }

        /* Strings Attach (Deactive) */
        if (!this.string_AptPhoto) {
            throw new Error(
                `level-manager-scene2(${this.object.name}): string_AptPhoto is not defined`
            );
        }
        for (const child of this.string_AptPhoto.children) {
            /* Deactive children */
            const string_AptPhotoMesh = child.getComponent(MeshComponent);
            if (string_AptPhotoMesh) {
                string_AptPhotoMesh.active = false;
            }
            /* Deactive children of children */
            for (const subchild of child.children) {
                const string_AptPhotoSubMesh = subchild.getComponent(MeshComponent);
                if (string_AptPhotoSubMesh) {
                    string_AptPhotoSubMesh.active = false;
                }
            }
        }

        /** Murder Images **/
        /* Deactive murderImages */
        if (!this.murderImages) {
            throw new Error(
                `level-manager-scene2(${this.object.name}): murderImages is not defined`
            );
        }
        for (const image of this.murderImages.children) {
            const murderImageMesh = image.getComponent(MeshComponent);
            if (!murderImageMesh) {
                throw new Error(
                    `level-manager-scene2(${this.object.name}): MeshComponent required in children of murderImage`
                );
            }
            murderImageMesh.active = false;

            const murderImagePhysx = image.getComponent(PhysXComponent);
            if (!murderImagePhysx) {
                throw new Error(
                    `level-manager-scene2(${this.object.name}): PhysXComponent required in children of murderImage`
                );
            }
            murderImagePhysx.active = false;
        }

        if (!this.doorCollision) {
            throw new Error(
                `level-manager-scene2(${this.object.name}): doorCollision is not defined`
            );
        }

        if (!this.doorPhoto) {
            throw new Error(
                `level-manager-scene2(${this.object.name}): doorPhoto is not defined`
            );
        }

        if (!this.player) {
            throw new Error(
                `level-manager-scene2(${this.object.name}): player is not defined`
            );
        }

        /* check depended components */
        const tempDoorCollisionPhysxComponent =
            this.doorCollision.getComponent(PhysXComponent);
        /* Increase crimeDocNum in case it's the last evidence that player picks */
        if (!tempDoorCollisionPhysxComponent) {
            throw new Error(
                `level-manager-scene2(${this.object.name}): PhysXComponent is missing on doorCollision`
            );
        }
        this.doorCollisionPhysxComponent = tempDoorCollisionPhysxComponent;

        const tempPlayerInventory = this.player.getComponent(Inventory);
        if (!tempPlayerInventory) {
            throw new Error(
                `level-manager-scene2(${this.object.name}): Inventory component is missing on player`
            );
        }
        this.playerInventory = tempPlayerInventory;

        const tempSeq01_02_TextBubbles = this.seq01_02_text.getComponent(TextBubbles);
        if (!tempSeq01_02_TextBubbles) {
            throw new Error(
                `level-manager-scene2(${this.object.name}): TextBubbles component is missing on seq01_02_text`
            );
        }
        this.seq01_02_TextBubbles = tempSeq01_02_TextBubbles;

        const tempseq01_04_InspectNote = this.seq01_04_notepad.getComponent(InspectNote);
        if (!tempseq01_04_InspectNote) {
            throw new Error(
                `level-manager-scene2(${this.object.name}): InspectNote component is missing on seq01_04_notepad`
            );
        }
        this.seq01_04_InspectNote = tempseq01_04_InspectNote;

        this.stringsTextBubbleNum = 1; /* The current number of TextBubble after all strings are active */

        setComponentsActive(this.crimeDocProgress, false);
    }

    sendMessage(msg: string) {
        switch (msg) {
            /* Player clicks on investigationBoard */
            case 'investigationBoardClicked':
                if (this.level2State === Level2State.pickedUpPhoto) {
                    this.investigationBoardPhysx!.active = false;
                    this.level2State = Level2State.gatheringFiles;
                    this.seq01_01_text!.active = false;
                    this.seq01_01_TextFade!.fadeOut();
                    this.seq01_killerPhotoPhysxComponent!.active = false;
                    this.seq01_killerPhotoTextBubbles!.displayNextLetter();
                } else if (this.level2State === Level2State.gatheringFiles) {
                    this.gatheringFilesBubbles!.displayNextLetter();
                }
                break;
            /** Sequence 1 **/
            /* After picking up photo, and clicking on Note */
            case 'seq01_01_photoOnBoard':
                this.level2State = Level2State.pickedUpPhoto;
                this.seq01_01();
                break;
            case 'seq01_02_killerPhotoClicked':
                /* On KillerPhoto Bubble Click */
                this.seq01_02();
                break;
            /* On KillerPhoto TextBubble01 End */
            case 'seq01_02_killerPhotoBubbleEnd':
                this.seq01_03();
                break;

            /* On KillerPhoto seq01_03_notepad End */
            case 'seq01_03_notepad_End':
                this.seq01_04();
                break;

            /* On KillerPhoto TextBubble02 End */
            case 'seq01_02_text_End':
                this.seq01_04_InspectNote!.startNote();
                this.investigationBoardPhysx!.active = true;
                break;

            /** Murder Images **/
            /* On StringsTextBubble End */

            case 'Strings_Text01_End':
            case 'Strings_Text02_End':
            case 'Strings_Text03_End':
                this.checkStrings(4);

                break;
            /* Bed - Scene2 End */
            case 'bed_scene2End':
                this.scene2End();
                break;
            default:
                console.error(`level-manager-scene2: Received unknown message :${msg}`);
                break;
        }
    }

    seq01_01() {
        /* Activate Texts & Collisions */
        this.seq01_01_TextComponent!.active = true;
        this.seq01_01_TextFade!.fadeIn(); /* Fade In */

        //this.seq01_killerPhotoPhysxComponent!.active = true;

        this.doorCollisionPhysxComponent!.active = false;
        this.investigationBoardPhysx!.active = true;
        this.playerInventory!.crimeDocNum += 1; /* Increase crimeDocNum */
        this.checkCrimeDocNum(this.doorPhoto!, this.playerInventory!.crimeDocNum);
    }

    seq01_02() {
        this.seq01_01_TextFade!.fadeOut(); /* Fade Out */
        //await delay(1); /* Wait till fade */
    }

    seq01_03() {
        const seq01_03_NotepadInspectNote =
            this.seq01_03_notepad!.getComponent(InspectNote);
        if (!seq01_03_NotepadInspectNote) {
            throw new Error(
                `level-manager-scene2(${this.object.name}): InspectNote is missing on seq01_03_NotepadInspectNote`
            );
        }
        seq01_03_NotepadInspectNote.startNote();
    }

    seq01_04() {
        setComponentsActive(this.crimeDocProgress, true);
        /* Active/Add ApartmentPhoto to InvestigationWall */
        for (const child of this.aptPhoto!.children) {
            child.getComponent(MeshComponent)!.active = true;
        }
        /* Strings Attach (Active) */
        for (const child of this.string_AptPhoto!.children) {
            const string_AptPhotoMesh = child.getComponent(MeshComponent);
            if (string_AptPhotoMesh) {
                string_AptPhotoMesh.active = true;
            }
            /* Deactive children of children */
            for (const subchild of child.children) {
                const string_AptPhotoSubMesh = subchild.getComponent(MeshComponent);
                if (string_AptPhotoSubMesh) {
                    string_AptPhotoSubMesh.active = true;
                }
            }
        }
        /* Start Text Bubble */
        this.seq01_02_TextBubbles!.displayNextLetter();
    }

    checkCrimeDocNum(inspectedObj: Object3D, crimeDocNum: number) {
        /* Collected all crimeDocs?, Gets called from 'inspect-world-object' */
        if (crimeDocNum == 6) {
            setComponentsActive(this.crimeDocProgress, false);
            this.level2State = Level2State.gatheredFiles;
            const inspectedObjInspectWorldObject =
                inspectedObj.getComponent(InspectWorldObject);
            if (!inspectedObjInspectWorldObject) {
                throw new Error(
                    `level-manager-scene2(${this.object.name}): inspect-world-object is missing on inspectedObj`
                );
            }
            //6
            inspectedObjInspectWorldObject.transitionToNote = true;
            inspectedObjInspectWorldObject.noteObj = this.notepad_crimeDocFinal;

            /* FadeIn Instruction Text */
            this.docsFinalTextComponent!.active = true;

            const docsFinalTextFade = this.docsFinal_boardText!.getComponent(TextFade);
            if (!docsFinalTextFade) {
                throw new Error(
                    `level-manager-scene2(${this.object.name}): TextFade is missing on docsFinal_boardText`
                );
            }
            docsFinalTextFade.fadeIn(); /* Fade In */

            /* Allow player to put photos on wall */
            const investigationBoardComponent =
                this.investigationBoard!.getComponent(InvestigationBoard);
            if (!investigationBoardComponent) {
                throw new Error(
                    `level-manager-scene2(${this.object.name}): InvestigationBoard component is missing on investigationBoard`
                );
            }
            investigationBoardComponent.currentState = 'collectedAllDocs';

            this.investigationBoardPhysx!.active = true;
        }
    }

    checkStrings(stringsActiveNum: number) {
        /* Continue only if all Strings are Active */
        if (stringsActiveNum != 4) {
            return;
        }
        /* Disable InvestigationBoard Collision, to not call old extra TextBubbles */
        this.investigationBoardPhysx!.active = false;

        this.murderImages!.children.forEach((child) => {
            const childPhysx = child.getComponent(PhysXComponent);
            if (!childPhysx) {
                throw new Error(
                    `level-manager-scene2(${this.object.name}): PhysXComponent is missing on murderImages children`
                );
            }
            childPhysx.active = false; /* Disable Physx Collision */

            const childTextFade = child.getComponent(TextFade);
            if (!childTextFade) {
                throw new Error(
                    `level-manager-scene2(${this.object.name}): TextFade is missing on murderImages children`
                );
            }
            childTextFade.active = false; /* To Stop Text Lerp */
            const childTextFadeObj = childTextFade.textObj!.getComponent(TextComponent);
            if (!childTextFadeObj) {
                throw new Error(
                    `level-manager-scene2(${this.object.name}): TextComponent is missing on murderImages children`
                );
            }
            const childTextFadeObjMaterial = childTextFadeObj.material as FlatMaterial;
            childTextFadeObjMaterial.color = [1, 1, 1, 0];
            /* Hide Image's Text */
        });

        /* Start TextsBubbles Order when all strings done */
        if (this.stringsTextBubbleNum == 1) {
            const textBubble01 = this.Strings_TextBubble01!.getComponent(TextBubbles);
            if (!textBubble01) {
                throw new Error(
                    `level-manager-scene2(${this.object.name}): TextBubbles is missing on Strings_TextBubble01`
                );
            }
            textBubble01.displayNextLetter(); /* Start Text01 */
        } else if (this.stringsTextBubbleNum == 2) {
            const textBubble2 = this.Strings_TextBubble02!.getComponent(TextBubbles);
            if (!textBubble2) {
                throw new Error(
                    `level-manager-scene2(${this.object.name}): TextBubbles is missing on Strings_TextBubble02`
                );
            }
            textBubble2.displayNextLetter(); /* Start Text02 */
            this.crimeCircleMesh!.active = true; /* Circle Map (Active) */
        } else if (this.stringsTextBubbleNum == 3) {
            const textBubble03 = this.Strings_TextBubble03!.getComponent(TextBubbles);
            if (!textBubble03) {
                throw new Error(
                    `level-manager-scene2(${this.object.name}): TextBubbles is missing on Strings_TextBubble03`
                );
            }
            textBubble03.displayNextLetter(); /* Start Text03 */
        } else if (this.stringsTextBubbleNum == 4) {
            const notepad5InspectNote =
                this.notepad5_InvestigationWall!.getComponent(InspectNote);
            if (!notepad5InspectNote) {
                throw new Error(
                    `level-manager-scene2(${this.object.name}): InspectNote is missing on notepad5_InvestigationWall`
                );
            }
            notepad5InspectNote.startNote();

            this.bedTextBubble!.disableClick = false; /* Active Bed */

            const investigationBoardComponent =
                this.investigationBoard!.getComponent(InvestigationBoard);
            investigationBoardComponent?.finishPuzzle();
        }

        this.stringsTextBubbleNum += 1; /* Switch to next TextBubble */
    }

    scene2End() {
        GA.sendProgressEvent('apartment');
        GameManagerInstance.loadScene(SceneNames.SceneCallToAction);
    }
}
